/**
 * WebGL2: the context, its programs and buffers, DPR and resize, recovery
 * from a lost context, and the quality governor that walks the degrade ladder.
 *
 * The renderer is a thin adapter over three passes (backdrop, bits + glow,
 * particles): every decision about what to draw was made by the conductor
 * and the frame packer. About a dozen draw calls at most; five in practice.
 * The governor is pure and tested; the rest is exercised by hand (README).
 */

import type { FrameModel, Renderer, Unsub } from '../contracts';
import { createBackdropPass, type BackdropPass } from './backdrop';
import { createBitsPass, type BitsPass } from './bits';
import { CAM } from './camera';
import { DARK_THEME, type Theme } from './palette';
import { createParticlePass, type ParticlePass } from './particles';

// ---------- the degrade ladder ----------

/** What each rung turns off, cumulatively (§4.5). */
export const LADDER = ['full', 'glow off', 'static backdrop', 'particles at 25 %', 'tilt off and DPR 1', 'Canvas2D'] as const;
export type Quality = 0 | 1 | 2 | 3 | 4 | 5;

export const DOWN_P90_MS = 22;
export const UP_P90_MS = 12;
export const WINDOW = 120;
export const CLIMB_AFTER_MS = 10_000;

/** The 90th percentile of some frame times. */
export const p90 = (xs: ArrayLike<number>, n = xs.length, scratch?: Float64Array): number => {
  if (n <= 0) return 0;
  const a = scratch && scratch.length >= n ? scratch.subarray(0, n) : new Float64Array(n);
  for (let i = 0; i < n; i++) a[i] = xs[i] as number;
  a.sort();
  return a[Math.min(n - 1, Math.floor(0.9 * (n - 1) + 0.5))] as number;
};

/**
 * The quality governor. Frame times go in (only from frames drawn at full
 * rate); a level comes out. If the p90 over 120 frames exceeds 22 ms it
 * steps down one rung; after 10 s with the p90 under 12 ms it climbs one.
 * The 10 s are wall time: sleeps between bursts of animation count toward
 * them as long as no burst on either side was slow.
 */
export class Governor {
  level: number;
  readonly max: number;
  private readonly win = new Float64Array(WINDOW);
  private readonly scratch = new Float64Array(WINDOW);
  private n = 0;
  private at = 0;
  private goodSince: number | null = null;
  /** When the level last moved: an idle loop climbs at most one rung per 10 s after it. */
  private movedAt: number | null = null;

  constructor(opts: { max?: number; start?: number } = {}) {
    this.max = opts.max ?? 5;
    this.level = Math.min(this.max, opts.start ?? 0);
  }

  /** One frame's time. Returns the new level when it changes, else null. */
  sample(ms: number, now: number): number | null {
    if (!Number.isFinite(ms) || ms <= 0) return null;
    this.win[this.at] = ms;
    this.at = (this.at + 1) % WINDOW;
    this.n = Math.min(WINDOW, this.n + 1);
    const q = p90(this.win, this.n, this.scratch);
    if (this.n >= WINDOW && q > DOWN_P90_MS) {
      this.goodSince = null;
      this.clearWindow();
      if (this.level < this.max) {
        this.level++;
        this.movedAt = now;
        return this.level;
      }
      return null;
    }
    if (this.n >= 30 && q < UP_P90_MS) {
      if (this.goodSince === null) this.goodSince = now;
      else if (now - this.goodSince >= CLIMB_AFTER_MS && this.level > 0) {
        this.level--;
        this.goodSince = now;
        this.movedAt = now;
        this.clearWindow();
        return this.level;
      }
    } else if (this.n >= 30) this.goodSince = null;
    return null;
  }

  /**
   * The loop went to sleep with nothing to animate. Idle time is neither slow
   * nor fast, so the window and the good run both stand: the frames of the
   * next burst add to them, and 10 s of wall time without a slow p90 climbs
   * a rung even when the animations come in short bursts (from rung 2 up,
   * the idle sea is off, so every burst is followed by a sleep).
   */
  pause(): void {}

  /**
   * The loop has slept with nothing to draw since the level last moved, 10 s ago or more: a screen with
   * nothing animating (the Loom, the Account after a busy Gallery) should not keep an old rung forever.
   * It climbs one rung, unless the frames still in the window were slow. Returns the new level, else null.
   */
  idle(now: number): number | null {
    if (this.level === 0 || this.movedAt === null || now - this.movedAt < CLIMB_AFTER_MS) return null;
    if (this.n > 0 && p90(this.win, this.n, this.scratch) > UP_P90_MS) return null;
    this.level--;
    this.movedAt = now;
    this.goodSince = null;
    this.clearWindow();
    return this.level;
  }

  /** A real gap (a hidden tab, a new renderer): the next samples start a fresh window and a fresh good run. */
  reset(): void {
    this.clearWindow();
    this.goodSince = null;
  }

  private clearWindow(): void {
    this.n = 0;
    this.at = 0;
  }
}

// ---------- the kit the passes build with ----------

export interface GlKit {
  readonly gl: WebGL2RenderingContext;
  link(vs: string, fs: string, name: string): WebGLProgram;
  uniforms<K extends string>(prog: WebGLProgram, names: readonly K[]): Record<K, WebGLUniformLocation | null>;
  /** Bind the shared unit quad (4 corners, a triangle strip) at `location` of the bound VAO. */
  bindQuad(location: number): void;
}

const compile = (gl: WebGL2RenderingContext, type: number, src: string, name: string): WebGLShader => {
  const sh = gl.createShader(type);
  if (!sh) throw new Error(`${name}: createShader failed`);
  gl.shaderSource(sh, src);
  gl.compileShader(sh);
  if (!gl.getShaderParameter(sh, gl.COMPILE_STATUS) && !gl.isContextLost()) {
    const log = gl.getShaderInfoLog(sh) ?? '';
    gl.deleteShader(sh);
    throw new Error(`${name} (${type === gl.VERTEX_SHADER ? 'vertex' : 'fragment'}): ${log}`);
  }
  return sh;
};

export const createKit = (gl: WebGL2RenderingContext): GlKit & { destroy(): void } => {
  const quad = gl.createBuffer();
  gl.bindBuffer(gl.ARRAY_BUFFER, quad);
  gl.bufferData(gl.ARRAY_BUFFER, new Float32Array([0, 0, 1, 0, 0, 1, 1, 1]), gl.STATIC_DRAW);
  gl.bindBuffer(gl.ARRAY_BUFFER, null);
  return {
    gl,
    link(vs, fs, name) {
      const v = compile(gl, gl.VERTEX_SHADER, vs, name);
      const f = compile(gl, gl.FRAGMENT_SHADER, fs, name);
      const p = gl.createProgram();
      if (!p) throw new Error(`${name}: createProgram failed`);
      gl.attachShader(p, v);
      gl.attachShader(p, f);
      gl.linkProgram(p);
      gl.deleteShader(v);
      gl.deleteShader(f);
      if (!gl.getProgramParameter(p, gl.LINK_STATUS) && !gl.isContextLost()) {
        const log = gl.getProgramInfoLog(p) ?? '';
        gl.deleteProgram(p);
        throw new Error(`${name} (link): ${log}`);
      }
      return p;
    },
    uniforms(prog, names) {
      const out = {} as Record<(typeof names)[number], WebGLUniformLocation | null>;
      for (const n of names) out[n] = gl.getUniformLocation(prog, n);
      return out;
    },
    bindQuad(location) {
      gl.bindBuffer(gl.ARRAY_BUFFER, quad);
      gl.enableVertexAttribArray(location);
      gl.vertexAttribPointer(location, 2, gl.FLOAT, false, 0, 0);
      gl.vertexAttribDivisor(location, 0);
    },
    destroy() {
      gl.deleteBuffer(quad);
    },
  };
};

// ---------- the renderer ----------

export interface FxRenderer extends Renderer {
  /** Set by fx from the governor (and the reduced-motion switch for the backdrop). */
  setQuality(q: Quality): void;
  setTheme(theme: Theme): void;
  setReduced(on: boolean): void;
  /** Told when the context is lost (true) and when it comes back (false). */
  onLost(fn: (lost: boolean) => void): Unsub;
  readonly kind: 'gl' | 'canvas2d';
  readonly drawCalls: number;
}

export const MAX_DPR = 2;

export const createGlRenderer = (): FxRenderer => {
  let canvas: HTMLCanvasElement | null = null;
  let gl: WebGL2RenderingContext | null = null;
  let kit: (GlKit & { destroy(): void }) | null = null;
  let backdrop: BackdropPass | null = null;
  let bits: BitsPass | null = null;
  let particles: ParticlePass | null = null;
  let quality: Quality = 0;
  let theme: Theme = DARK_THEME;
  let reduced = false;
  let lost = false;
  let size = { w: 1, h: 1, dpr: 1 };
  let drawCalls = 0;
  const qualityFns = new Set<(q: number) => void>();
  const lostFns = new Set<(lost: boolean) => void>();

  const build = (): boolean => {
    if (!gl) return false;
    try {
      kit = createKit(gl);
      backdrop = createBackdropPass(gl, kit);
      bits = createBitsPass(gl, kit);
      particles = createParticlePass(gl, kit);
      applySize();
      return true;
    } catch (e) {
      console.warn('[fx] WebGL2 setup failed; falling back to Canvas2D', e);
      teardown();
      return false;
    }
  };

  const teardown = () => {
    backdrop?.destroy();
    bits?.destroy();
    particles?.destroy();
    kit?.destroy();
    backdrop = bits = particles = null;
    kit = null;
  };

  const dprNow = () => (quality >= 4 ? 1 : Math.min(MAX_DPR, Math.max(1, size.dpr)));

  const applySize = () => {
    if (!canvas || !gl) return;
    const dpr = dprNow();
    const w = Math.max(1, Math.round(size.w * dpr)), h = Math.max(1, Math.round(size.h * dpr));
    if (canvas.width !== w) canvas.width = w;
    if (canvas.height !== h) canvas.height = h;
    backdrop?.resize(w, h, dpr);
  };

  const onLostEvent = (e: Event) => {
    e.preventDefault();
    lost = true;
    teardown();
    for (const fn of lostFns) fn(true);
  };
  const onRestored = () => {
    lost = false;
    if (build()) {
      bits?.forceFull();
      for (const fn of lostFns) fn(false);
    }
  };

  const r: FxRenderer = {
    kind: 'gl',
    mount(c) {
      canvas = c;
      try {
        gl = c.getContext('webgl2', { alpha: false, antialias: false, depth: false, stencil: false, premultipliedAlpha: true, preserveDrawingBuffer: false, powerPreference: 'high-performance' });
      } catch {
        gl = null;
      }
      if (!gl) return false;
      c.addEventListener('webglcontextlost', onLostEvent);
      c.addEventListener('webglcontextrestored', onRestored);
      if (!build()) {
        c.removeEventListener('webglcontextlost', onLostEvent);
        c.removeEventListener('webglcontextrestored', onRestored);
        gl = null;
        return false;
      }
      return true;
    },
    draw(frame: FrameModel) {
      if (!gl || lost || !backdrop || !bits || !particles) return;
      drawCalls = 0;
      const W = canvas?.width ?? 1, H = canvas?.height ?? 1;
      gl.disable(gl.DEPTH_TEST);
      gl.disable(gl.CULL_FACE);
      drawCalls += backdrop.draw(frame, theme, { animate: quality < 2 && !reduced, viewport: [W, H] });
      gl.viewport(0, 0, W, H);
      gl.enable(gl.BLEND);
      gl.blendFunc(gl.ONE, gl.ONE_MINUS_SRC_ALPHA);
      bits.upload(frame);
      const time = frame.camera[CAM.time] ?? 0;
      drawCalls += bits.draw(frame, theme, time);
      if (quality < 1) {
        gl.blendFunc(gl.ONE, theme.dark ? gl.ONE : gl.ONE_MINUS_SRC_ALPHA);
        drawCalls += bits.drawGlow(frame, theme.dark ? 1 : 0.45);
      }
      if (frame.particleCount > 0) {
        particles.draw(frame.particles, frame.particleCount, frame.camera, theme.dark);
        drawCalls++;
      }
      gl.disable(gl.BLEND);
    },
    resize(w, h, dpr) {
      size = { w, h, dpr };
      applySize();
    },
    get quality() {
      return quality;
    },
    onQuality(fn) {
      qualityFns.add(fn);
      return () => qualityFns.delete(fn);
    },
    setQuality(q) {
      if (q === quality) return;
      const dprChange = (q >= 4) !== (quality >= 4);
      quality = q;
      if (dprChange) applySize();
      backdrop?.invalidate();
      for (const fn of qualityFns) fn(q);
    },
    setTheme(t) {
      theme = t;
      backdrop?.invalidate();
    },
    setReduced(on) {
      reduced = on;
      backdrop?.invalidate();
    },
    onLost(fn) {
      lostFns.add(fn);
      return () => lostFns.delete(fn);
    },
    get drawCalls() {
      return drawCalls;
    },
    destroy() {
      teardown();
      canvas?.removeEventListener('webglcontextlost', onLostEvent);
      canvas?.removeEventListener('webglcontextrestored', onRestored);
      gl = null;
      canvas = null;
      qualityFns.clear();
      lostFns.clear();
    },
  };
  return r;
};
